/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { FilterFn, OnChangeFn, PaginationState } from '@tanstack/react-table';
import { SdsDivProps } from '../Component.props';
import { TableContent } from './Table.defs';

/**
 * The props for `<Table />`.
 */
export interface TableProps extends SdsDivProps {
  /**
   * Text to display for empty table content.
   */
  emptyText?: string;

  /**
   * Toggle slim appearance for the table.
   */
  isSlim?: boolean;

  /**
   * Toggle table pagination.
   */
  isPaginated?: boolean;

  /**
   * Toggle table search.
   */
  isSearchable?: boolean;

  /**
   * Toggle table sorting.
   */
  isSortable?: boolean;

  /**
   * Placeholder text for the table search field.
   */
  searchPlaceholderText?: string;

  /**
   * Specify the default rows per page selection.
   */
  perPageDefault?: number;

  /**
   * Specify the rows per page select field.
   */
  perPageOptions?: Array<number>;

  /**
   * Text to accompany the rows per page select field.
   */
  perPageText?: string;

  /**
   * Table content.
   */
  tableContent: TableContent;

  /**
   * Pagination state.
   */
  pagination?: {
    pageIndex: number;
    pageSize: number;
  };

  /**
   * Set pagination state.
   */
  setPagination?: OnChangeFn<PaginationState>;

  /**
   * Custom row filtering function.
   * TData - The type of the row data.
   */
  customFilteringFn?: FilterFn<unknown>;
}

/**
 * The default prop values for `<Table />`.
 */
export const tableDefaults = {
  emptyText: `No entries found.`,
  isPaginated: true,
  isSearchable: false,
  isSlim: false,
  isSortable: false,
  perPageDefault: 10,
  perPageOptions: [5, 10, 20, 50],
  perPageText: `Rows per page:`,
  searchPlaceholderText: `Search the table`,
};
